import React from 'react';
import Helmet from 'react-helmet';

import { SEO_DEFAULTS } from '@core/config';

interface ISeoProps {
  title?: string;
  description?: string;
  url?: string;
  keywords?: string;
  /** e.g. "noindex, follow" for alias pages that should not compete with canonical URL */
  robots?: string;
}

/**
 * Renders Helmet only when page-specific SEO is provided.
 * When no title/description/keywords are passed, we do not touch the head,
 * so the static meta from index.html (public/SEO) are used.
 */
export const Seo: React.FC<ISeoProps> = ({
  url,
  title: propTitle,
  description,
  keywords,
  robots,
}) => {
  const hasOverrides = propTitle != null || description != null || keywords != null || robots != null;
  if (!hasOverrides) {
    return null;
  }

  const href =
    url ??
    (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '');
  const title = propTitle ? `${propTitle} | ${SEO_DEFAULTS.titleShort}` : SEO_DEFAULTS.title;

  return (
    <Helmet
      title={title}
      htmlAttributes={{ lang: 'pl' }}
      meta={getMetaTags({
        url: href,
        title,
        description: description ?? SEO_DEFAULTS.description,
        keywords: keywords ?? SEO_DEFAULTS.keywords,
        robots,
      })}
    />
  );
};

const getMetaTags = ({
  title,
  url,
  description = SEO_DEFAULTS.description,
  keywords = SEO_DEFAULTS.keywords,
  robots,
}: {
  title: string;
  url: string;
  description?: string;
  keywords?: string;
  robots?: string;
}): JSX.IntrinsicElements['meta'][] => [
  { name: 'description', content: description },
  { name: 'keywords', content: keywords },
  ...(robots ? [{ name: 'robots' as const, content: robots }] : []),

  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:title', content: title },
  { name: 'twitter:description', content: description },
  { name: 'twitter:image', content: SEO_DEFAULTS.image },

  { property: 'og:type', content: 'website' },
  { property: 'og:title', content: title },
  { property: 'og:url', content: url },
  { property: 'og:image', content: SEO_DEFAULTS.image },
  { property: 'og:description', content: description },
  { property: 'fb:app_id', content: '102401894455118' },
];
